import React, { useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Box,
    Button,
    Card,
    Heading,
    HStack,
    Input,
    List,
    SimpleGrid,
    Stack,
    Text,
    Textarea,
} from '@chakra-ui/react';
import {
    AlertTriangleIcon,
    CheckCircleIcon,
    InfoIcon,
} from '../components/Icons';
import { api, SystemStatusUpload, SystemStatusUploadResponse } from '../api/api';
import { FormControl, FormHelperText, FormLabel } from '../components/FormControls';
import SimpleModal from '../components/SimpleModal';

const COOKIE_KEY = 'status-upload-draft';

const emptyForm: SystemStatusUpload = {
    companyName: '',
    vatNumber: '',
    crnNumber: '',
    machineKey: '',
    encryptedKey: '',
    statusJson: '',
    currentVersion: ''
};

const rand = (len = 6) => Math.random().toString(36).substring(2, 2 + len);

function makeRandomForm(): SystemStatusUpload {
    const companyName = `Company ${rand(5)}`;
    const vatNumber = `VAT${Math.floor(Math.random() * 100000)}`;
    const crnNumber = `CRN${Math.floor(Math.random() * 100000)}`;
    const machineKey = rand(12);
    const currentVersion = `1.${Math.floor(Math.random() * 10)}.${Math.floor(Math.random() * 10)}`;
    const statusJson = JSON.stringify({
        companyName,
        vatNumber,
        crnNumber,
        machineKey,
        currentVersion,
        systemStatus: {
            cpu: Math.floor(Math.random() * 100),
            ram: Math.floor(Math.random() * 100),
            disk: Math.floor(Math.random() * 100),
            status: ['ok', 'warning', 'error'][Math.floor(Math.random() * 3)],
            timestamp: new Date().toISOString()
        }
    }, null, 2);

    return {
        companyName,
        vatNumber,
        crnNumber,
        machineKey,
        encryptedKey: '',
        statusJson,
        currentVersion
    };
}

function syncStatusJson(form: SystemStatusUpload, currentJson: string): string {
    const payload = buildStatusPayload(form, currentJson);
    return JSON.stringify(payload, null, 2);
}

function buildStatusPayload(form: SystemStatusUpload, currentJson: string, overrideTimestamp?: string) {
    const randomMetric = () => Math.floor(Math.random() * 100);

    let existingStatus: any = {
        cpu: randomMetric(),
        ram: randomMetric(),
        disk: randomMetric(),
        status: 'ok',
        timestamp: new Date().toISOString()
    };

    try {
        const parsed = JSON.parse(currentJson || '{}');
        if (parsed && typeof parsed === 'object' && parsed.systemStatus) {
            const { cpu, ram, disk, status, timestamp } = parsed.systemStatus;
            existingStatus = {
                ...existingStatus,
                ...(cpu !== undefined ? { cpu } : {}),
                ...(ram !== undefined ? { ram } : {}),
                ...(disk !== undefined ? { disk } : {}),
                ...(status !== undefined ? { status } : {}),
                ...(timestamp !== undefined ? { timestamp } : {})
            };
        }
    } catch {
        // keep defaults
    }

    if (overrideTimestamp) {
        existingStatus.timestamp = overrideTimestamp;
    }

    return {
        companyName: form.companyName,
        vatNumber: form.vatNumber,
        crnNumber: form.crnNumber,
        machineKey: form.machineKey || undefined,
        encryptedKey: form.encryptedKey || undefined,
        currentVersion: form.currentVersion || undefined,
        systemStatus: existingStatus
    };
}

function getCookie(name: string) {
    if (typeof document === 'undefined') return '';
    const match = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith(name + '='));
    return match ? decodeURIComponent(match.split('=')[1]) : '';
}

function setCookie(name: string, value: string, days = 30) {
    if (typeof document === 'undefined') return;
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
}

export default function SystemStatusUploadPage() {
    const [form, setForm] = useState<SystemStatusUpload>(emptyForm);
    const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [systemInfo, setSystemInfo] = useState<SystemStatusUploadResponse['systemInfo'] | null>(null);
    const [popup, setPopup] = useState<{ heading: string; content: string; isError?: boolean } | null>(null);

    // Load draft from cookie on mount
    useEffect(() => {
        try {
            const raw = getCookie(COOKIE_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                setForm({ ...emptyForm, ...parsed });
            }
        } catch {
            setForm(emptyForm);
        }
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        // If user edits the JSON directly, store as-is.
        if (name === 'statusJson') {
            const next = { ...form, statusJson: value } as SystemStatusUpload;
            setForm(next);
            setCookie(COOKIE_KEY, JSON.stringify(next));
            return;
        }

        const nextForm = { ...form, [name]: value } as SystemStatusUpload;
        const syncedJson = syncStatusJson(nextForm, nextForm.statusJson);
        const finalForm = { ...nextForm, statusJson: syncedJson };
        setForm(finalForm);
        setCookie(COOKIE_KEY, JSON.stringify(finalForm));
    };

    const setTimestamp = (mode: 'now' | 'random' | 'yesterday' | 'lastWeek' | 'lastMonth' | 'lastYear' | 'twoYears') => {
        const now = new Date();
        let target = new Date(now);

        switch (mode) {
            case 'random': {
                const daysBack = Math.floor(Math.random() * 365);
                const minutesBack = Math.floor(Math.random() * 24 * 60);
                target = new Date(now.getTime() - (daysBack * 86400000) - (minutesBack * 60000));
                break;
            }
            case 'yesterday':
                target = new Date(now.getTime() - 86400000);
                break;
            case 'lastWeek':
                target = new Date(now.getTime() - 7 * 86400000);
                break;
            case 'lastMonth':
                target.setMonth(target.getMonth() - 1);
                break;
            case 'lastYear':
                target.setFullYear(target.getFullYear() - 1);
                break;
            case 'twoYears':
                target.setFullYear(target.getFullYear() - 2);
                break;
            case 'now':
            default:
                target = now;
        }

        const iso = target.toISOString();
        const payload = buildStatusPayload(form, form.statusJson, iso);
        const statusJson = JSON.stringify(payload, null, 2);
        const next = { ...form, statusJson };
        setForm(next);
        setCookie(COOKIE_KEY, JSON.stringify(next));
    };

    const randomizeMetrics = () => {
        const randomMetric = () => Math.floor(Math.random() * 100);
        const payload = buildStatusPayload(form, form.statusJson);
        payload.systemStatus = {
            ...payload.systemStatus,
            cpu: randomMetric(),
            ram: randomMetric(),
            disk: randomMetric()
        };

        const statusJson = JSON.stringify(payload, null, 2);
        const next = { ...form, statusJson };
        setForm(next);
        setCookie(COOKIE_KEY, JSON.stringify(next));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const hasMachineKey = !!form.machineKey?.trim();
        const hasEncryptedKey = !!form.encryptedKey?.trim();
        if (!hasMachineKey && !hasEncryptedKey) {
            setResult({ type: 'error', message: 'Provide either Machine Key or Encrypted Key before uploading.' });
            return;
        }

        try {
            const syncedJson = syncStatusJson(form, form.statusJson);
            const payload: SystemStatusUpload = {
                ...form,
                machineKey: form.machineKey?.trim(),
                encryptedKey: form.encryptedKey?.trim(),
                companyName: form.companyName.trim(),
                vatNumber: form.vatNumber.trim(),
                crnNumber: form.crnNumber?.trim(),
                statusJson: syncedJson
            };

            const res = await api.systemStatus.upload(payload);
            setResult({ type: 'success', message: res.message });
            setSystemInfo(res.systemInfo);
            // Keep entered keys after success; refresh only statusJson with trimmed values
            const refreshedJson = syncStatusJson(payload, payload.statusJson);
            const nextForm = { ...payload, statusJson: refreshedJson } as SystemStatusUpload;
            setForm(nextForm);
            setCookie(COOKIE_KEY, JSON.stringify(nextForm));
        } catch (err: any) {
            setResult({ type: 'error', message: err?.response?.data?.error || err.message });
            setSystemInfo(null);
        }
    };

    const formatJson = () => {
        try {
            const parsed = JSON.parse(form.statusJson);
            const pretty = JSON.stringify(parsed, null, 2);
            const next = { ...form, statusJson: pretty } as SystemStatusUpload;
            setForm(next);
            setCookie(COOKIE_KEY, JSON.stringify(next));
        } catch (error) {
            // Invalid JSON, do nothing
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const isExpiringWarning = (dateString: string) => {
        const expiryDate = new Date(dateString);
        const today = new Date();
        const daysUntilExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return daysUntilExpiry <= 30;
    };

    return (
        <Stack gap={6} maxW="900px" w="100%" mx="auto">
            <Card.Root overflow="hidden">
                <Card.Body p={{ base: 2, md: 3 }}>
                    <Stack gap={3}>
                        <Heading size="lg">Upload System Status</Heading>
                        <form onSubmit={handleSubmit}>
                            <Stack gap={3}>
                                <FormControl isRequired>
                                    <FormLabel>
                                        <Text fontWeight="bold">Company Name</Text>
                                    </FormLabel>
                                    <Input
                                        id="companyName"
                                        name="companyName"
                                        value={form.companyName}
                                        onChange={handleChange}
                                        placeholder="Company linked to this device"
                                    />
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel>
                                        <Text fontWeight="bold">VAT Number</Text>
                                    </FormLabel>
                                    <Input
                                        id="vatNumber"
                                        name="vatNumber"
                                        value={form.vatNumber}
                                        onChange={handleChange}
                                        placeholder="VAT number (required)"
                                    />
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel>
                                        <Text fontWeight="bold">CRN Number</Text>
                                    </FormLabel>
                                    <Input
                                        id="crnNumber"
                                        name="crnNumber"
                                        value={form.crnNumber}
                                        onChange={handleChange}
                                        placeholder="CRN number (required)"
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>
                                        <Text fontWeight="bold">Machine Key (optional; server can generate on first registration)</Text>
                                    </FormLabel>
                                    <Input
                                        id="machineKey"
                                        name="machineKey"
                                        value={form.machineKey || ''}
                                        onChange={handleChange}
                                        placeholder="Provide machine key for first-time registration"
                                    />
                                    <FormHelperText display="flex" alignItems="center" gap={2}>
                                        <InfoIcon size={14} /> Provide if you have one; otherwise the server will generate during first registration. It will be included in the status payload.
                                    </FormHelperText>
                                </FormControl>

                                <FormControl>
                                    <FormLabel>
                                        <Text fontWeight="bold">Encrypted Key (optional)</Text>
                                    </FormLabel>
                                    <Input
                                        id="encryptedKey"
                                        name="encryptedKey"
                                        value={form.encryptedKey || ''}
                                        onChange={handleChange}
                                        placeholder="Composite key of machine + VAT + CRN"
                                    />
                                    <FormHelperText display="flex" alignItems="center" gap={2}>
                                        <InfoIcon size={14} /> If left blank, the server derives it from machine key + VAT + CRN. It will be included in the status payload.
                                    </FormHelperText>
                                </FormControl>

                                <FormControl>
                                    <FormLabel>
                                        <Text fontWeight="bold">Current Version</Text>
                                    </FormLabel>
                                    <Input
                                        id="currentVersion"
                                        name="currentVersion"
                                        value={form.currentVersion || ''}
                                        onChange={handleChange}
                                        placeholder="e.g., 1.2.3"
                                    />
                                    <FormHelperText display="flex" alignItems="center" gap={2}>
                                        <InfoIcon size={14} /> Enter the current software version running on the device.
                                    </FormHelperText>
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel>
                                        <Text fontWeight="bold">Status JSON</Text>
                                    </FormLabel>
                                    <Textarea
                                        id="statusJson"
                                        name="statusJson"
                                        value={form.statusJson}
                                        onChange={handleChange}
                                        rows={14}
                                        fontFamily="mono"
                                        fontSize="sm"
                                        placeholder="Enter complete status data in JSON format"
                                    />
                                    <FormHelperText display="flex" alignItems="center" gap={2}>
                                        <InfoIcon size={14} /> Include company identifiers and the full system status payload.
                                    </FormHelperText>
                                    <HStack gap={2} mt={2} flexWrap="wrap" alignItems="flex-start">
                                        <Button
                                            onClick={formatJson}
                                            variant="solid"
                                            colorPalette="blue"
                                            size="sm"
                                        >
                                            Format JSON
                                        </Button>
                                        <Button
                                            onClick={randomizeMetrics}
                                            variant="solid"
                                            colorPalette="blue"
                                            size="sm"
                                        >
                                            Randomize CPU/RAM/Disk
                                        </Button>
                                    </HStack>
                                    <HStack gap={2} mt={2} flexWrap="wrap">
                                        <Button size="xs" variant="solid" colorPalette="teal" onClick={() => setTimestamp('now')}>Now</Button>
                                        <Button size="xs" variant="solid" colorPalette="teal" onClick={() => setTimestamp('random')}>Random</Button>
                                        <Button size="xs" variant="solid" colorPalette="teal" onClick={() => setTimestamp('yesterday')}>Yesterday</Button>
                                        <Button size="xs" variant="solid" colorPalette="teal" onClick={() => setTimestamp('lastWeek')}>Last Week</Button>
                                        <Button size="xs" variant="solid" colorPalette="teal" onClick={() => setTimestamp('lastMonth')}>Last Month</Button>
                                        <Button size="xs" variant="solid" colorPalette="teal" onClick={() => setTimestamp('lastYear')}>Last Year</Button>
                                        <Button size="xs" variant="solid" colorPalette="teal" onClick={() => setTimestamp('twoYears')}>2 Years Ago</Button>
                                    </HStack>
                                </FormControl>

                                <HStack gap={3} flexWrap="wrap">
                                    <Button
                                        type="submit"
                                        colorPalette="brand"
                                        size="lg"
                                    >
                                        Upload System Status
                                    </Button>
                                </HStack>
                            </Stack>
                        </form>

                        {result && (
                            <Alert.Root status={result.type} borderRadius="md">
                                <Alert.Content>
                                    <Stack gap={1}>
                                        <Alert.Title>
                                            {result.type === 'success' ? 'Upload Successful!' : 'Upload Failed!'}
                                        </Alert.Title>
                                        <Alert.Description>{result.message}</Alert.Description>
                                    </Stack>
                                </Alert.Content>
                            </Alert.Root>
                        )}

                        {systemInfo && (
                            <Card.Root borderColor="brand.100" borderWidth="1px">
                                <Card.Header p={{ base: 2, md: 3 }}>
                                    <HStack gap={2} color="brand.600" fontWeight="semibold">
                                        <InfoIcon />
                                        <Text>System Information</Text>
                                    </HStack>
                                </Card.Header>
                                <Card.Body p={{ base: 2, md: 3 }}>
                                    <Stack gap={2}>
                                        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                                            <Box>
                                                <Text fontSize="sm" color="gray.500">
                                                    System Name
                                                </Text>
                                                <Text fontWeight="bold">{systemInfo.systemName}</Text>
                                            </Box>
                                            <Box>
                                                <Text fontSize="sm" color="gray.500">
                                                    License Status
                                                </Text>
                                                <Badge colorPalette={systemInfo.isActive ? 'green' : 'red'} display="inline-flex" alignItems="center" gap={1} mt={1}>
                                                    <CheckCircleIcon size={14} />
                                                    {systemInfo.isActive ? 'Active' : 'Inactive'}
                                                </Badge>
                                            </Box>
                                        </SimpleGrid>
                                        <Box>
                                            <Text fontSize="sm" color="gray.500">
                                                License Expiry Date
                                            </Text>
                                            <HStack gap={3} align="center">
                                                <Text fontWeight="bold">{formatDate(systemInfo.licenseExpiryDate)}</Text>
                                                {systemInfo.isActive && isExpiringWarning(systemInfo.licenseExpiryDate) && (
                                                    <Badge colorPalette="yellow" color="yellow.800" display="inline-flex" alignItems="center" gap={1}>
                                                        <AlertTriangleIcon size={14} /> Expiring Soon
                                                    </Badge>
                                                )}
                                            </HStack>
                                        </Box>
                                    </Stack>
                                </Card.Body>
                            </Card.Root>
                        )}

                        <Card.Root variant="outline" bg="gray.50">
                            <Card.Body p={{ base: 2, md: 3 }}>
                                <HStack gap={2} mb={2} color="brand.600">
                                    <InfoIcon />
                                    <Text fontWeight="semibold">JSON Format Example</Text>
                                </HStack>
                                <Box
                                    as="pre"
                                    fontSize={{ base: 'xs', md: 'sm' }}
                                    fontFamily="mono"
                                    whiteSpace="pre-wrap"
                                    wordBreak="break-word"
                                    bg="white"
                                    borderRadius="md"
                                    p={{ base: 2, md: 4 }}
                                    borderWidth="1px"
                                    overflowX="auto"
                                >{`{
  "companyName": "ABC Corporation",
  "vatNumber": "VAT123456789",
  "crnNumber": "CRN987654321",
  "systemStatus": {
    "cpu": 85,
    "ram": 67,
    "disk": 45,
    "status": "ok",
    "timestamp": "2024-01-15T10:30:00Z"
  }
}`}</Box>
                            </Card.Body>
                        </Card.Root>

                        <Alert.Root status="info" variant="subtle" borderRadius="md">
                            <Alert.Content>
                                <Stack gap={2}>
                                    <Heading size="sm">Security & Validation</Heading>
                                    <List.Root as="ul" gap={1} fontSize="sm" ps={4}>
                                        <List.Item _marker={{ color: 'blue.500' }}>Composite key = machine key + VAT + CRN, derived server-side if not provided</List.Item>
                                        <List.Item _marker={{ color: 'blue.500' }}>Registration is auto-created on first call when machine key is present</List.Item>
                                        <List.Item _marker={{ color: 'blue.500' }}>Subsequent calls can omit the machine key; VAT + CRN still required</List.Item>
                                        <List.Item _marker={{ color: 'blue.500' }}>License expiry is verified before accepting uploads</List.Item>
                                        <List.Item _marker={{ color: 'blue.500' }}>Response includes license status and expiry details</List.Item>
                                    </List.Root>
                                </Stack>
                            </Alert.Content>
                        </Alert.Root>
                    </Stack>
                </Card.Body>
            </Card.Root>

            <SimpleModal
                isOpen={!!popup}
                onClose={() => setPopup(null)}
                title={popup?.heading}
                size="lg"
            >
                <Box
                    as="pre"
                    whiteSpace="pre-wrap"
                    wordBreak="break-word"
                    fontSize="sm"
                    bg="gray.50"
                    p={4}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={popup?.isError ? 'red.200' : 'gray.100'}
                    color={popup?.isError ? 'red.700' : 'gray.800'}
                >
                    {popup?.content}
                </Box>
            </SimpleModal>
        </Stack>
    );
}
